import { useMemo, type CSSProperties } from "react";
import type { Client } from "@/api/schemas";
import { deriveClientAccent } from "@/utils/clientAccent";
import { getAdsBasePath } from "@/utils/basePath";
import { iniciales } from "@/utils/clientes";
import styles from "./ClientBrand.module.css";

export type ClientBrandSize = 20 | 24 | 28 | 32 | 48 | 56;

interface ClientBrandProps {
  client: Pick<Client, "name" | "logo_url" | "accent_color">;
  size?: ClientBrandSize;
}

/** El logo vive en la API del panel: bajo el puente de Safent la ruta lleva el prefijo `/ads`. */
function logoSrc(path: string): string {
  return `${getAdsBasePath()}${path}`;
}

/**
 * Marca del cliente: su logo sobre papel o, sin logo, un monograma con sus iniciales. El nombre va al
 * lado, en quien lo usa: la imagen es decorativa. Es el único sitio donde aparece el color de OTRO
 * cliente (cada avatar lleva el acento propio), por eso se calcula con el acento del cliente y no con
 * `--color-accent`, que es el del cliente en pantalla.
 */
export function ClientBrand({ client, size = 32 }: ClientBrandProps) {
  const vars = useMemo(() => {
    const accent = deriveClientAccent(client.accent_color);
    return {
      "--brand-fill-light": accent.light.fill,
      "--brand-on-light": accent.light.on,
      "--brand-fill-dark": accent.dark.fill,
      "--brand-on-dark": accent.dark.on,
    } as CSSProperties;
  }, [client.accent_color]);
  const dimension = { width: size, height: size };

  if (client.logo_url) {
    return (
      <span className={styles.logo} style={dimension} aria-hidden="true">
        <img className={styles.image} src={logoSrc(client.logo_url)} alt="" width={size} height={size} loading="lazy" decoding="async" />
      </span>
    );
  }
  return (
    <span className={styles.monogram} style={{ ...dimension, ...vars, fontSize: size >= 48 ? "var(--fs-lg)" : size >= 28 ? "var(--fs-sm)" : "var(--fs-2xs)" }} aria-hidden="true" data-testid="client-monogram">
      {iniciales(client.name)}
    </span>
  );
}
